import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, valuesOf } from '../skillUsage';

export const headlineSlamSkill: CodeoSkill = {
    name: SKILL_NAMES.headlineSlam,
    kind: 'codeo',
    description:
        'Land a short line hard — an oversized word arriving fast and stopping dead, with the ' +
        'frame reacting — for a verdict, a punchline, a price, or a single-word declaration.',
    documentUsesSkill: check(
        (script) =>
            /ease:\s*['"](?:power4|expo)\.out/.test(script) &&
            (valuesOf(script, 'scale').some((value) => value > 1) ||
                valuesOf(script, 'y').some((value) => Math.abs(value) >= 40) ||
                valuesOf(script, 'x').some((value) => Math.abs(value) >= 40)),
    ),
    instructions: `# Headline Slam

One short line arrives fast, overshoots almost not at all, and stops dead. Everything else in the frame flinches. It is the loudest entrance in the library and it works exactly once per video — a slam that happens three times is a template, not an emphasis.

The impact does not come from the word's own motion. It comes from what *else* reacts: a rule that jumps, a background that pulses, the support copy shifted a few pixels. A word arriving quickly with nothing responding to it just looks fast.

## Markup

Set it far larger than anything else in the frame. A slam at a polite size reads as a glitch.

\`\`\`html
<div class="absolute inset-0 flex flex-col justify-center items-center gap-10 p-24">
  <div id="slam-flash" style="position: absolute; inset: 0; background: var(--l5-color-2, #38bdf8);"></div>
  <div id="slam-word" class="text-center" style="font-family: var(--l5-font-0), sans-serif; font-size: 240px; font-weight: 900; letter-spacing: -0.03em; line-height: 0.9; color: var(--l5-color-0, #101820)">FREE</div>
  <div id="slam-support" class="text-center" style="font-family: var(--l5-font-0), sans-serif; font-size: 42px; font-weight: 500; color: var(--l5-color-1, #5b6472)">Every plan. No card.</div>
</div>
\`\`\`

## Timeline code

The word travels a short distance very quickly. \`power4.out\` is the shape of an impact: almost all the distance is covered in the first third of the tween.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const HIT = 0.55;

gsap.set('#slam-word', { scale: 1.35, opacity: 0 });
gsap.set('#slam-flash', { opacity: 0 });
gsap.set('#slam-support', { y: 0, opacity: 0 });

tl.to('#slam-word', { scale: 1, opacity: 1, duration: 0.32, ease: 'power4.out' }, HIT);

// The reaction — this is what makes it land.
tl.to('#slam-flash', { opacity: 0.16, duration: 0.06, ease: 'none' }, HIT + 0.04);
tl.to('#slam-flash', { opacity: 0, duration: 0.34, ease: 'power2.out' }, HIT + 0.1);
tl.fromTo('#slam-support', { y: 14 }, { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' }, HIT + 0.12);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Arrival vector**: \`scale: 1.35\` reads as coming at the viewer. \`y: -60\` reads as dropping in and is heavier; \`x\` reads as being thrown and is the weakest of the three.
- **Flash**: 0.12–0.2 opacity for a light template, and use a palette color rather than white — a white flash on a light background is invisible and on a dark one is violent.
- **Shake instead of flash**: nudge the whole stage 6px and settle it (\`x: 6\` → \`0\`, \`power3.out\`). Reads as physical rather than electrical.
- **On the word**: if the scene has narration, \`HIT\` is the spoken onset of the word, not a moment near it. The whole technique fails by a tenth of a second.
- **Letter-level**: splitting into per-character spans and slamming with a 0.02s stagger reads as a stamp rather than a hit — different move, same family.
- **Quiet version**: drop the flash and keep the overshoot-free arrival for a verdict that should feel certain rather than loud.

## Pitfalls

- Don't overshoot. \`back.out\` on a slam turns impact into bounce, which reads as playful and undoes the whole point.
- The word is \`scale\`-tweened, so it must not carry a CSS \`transform\`; center it with \`left/right: 0\` and \`text-align\`.
- Keep it to one or two words. A slammed sentence is just a fast entrance.
- One slam per video. The second one costs the first its impact, and the third makes the piece feel like a trailer.
- Follow it with something. A slam that lands and then holds for four seconds dies on screen — give the support line an entrance and the frame an ambient layer.
- \`#slam-flash\` covers the whole frame, so it must sit above the background and below the word in DOM order, or it will wash the word out.
- The support line's \`fromTo\` starts at \`opacity: 0\` (from the \`gsap.set\`) with a visible \`y\`, and its from-state is applied at build time — don't add \`immediateRender: false\`.`,
};
